import { useContext, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { HouseholdContext } from "./HouseholdInfo";
import {
  SlantPerson,
  SwitchDecision,
  useHouseholdPeople,
} from "./switch-to-slant/api";
import SwitchToSlantStep1 from "./switch-to-slant/SwitchToSlantStep1";
import SwitchToSlantStep2 from "./switch-to-slant/SwitchToSlantStep2";
import SwitchToSlantStep3, {
  ReviewRow,
} from "./switch-to-slant/SwitchToSlantStep3";
import SwitchToSlantStep4 from "./switch-to-slant/SwitchToSlantStep4";

/**
 * Switch-to-Slant wizard shell (APP-1303) — STATELESS.
 *
 * Drives the four-step Wealthbox → Slant switch. There is no server-side state
 * machine: this shell holds ALL wizard state in React (the chosen Slant
 * household, the per-person decisions, and the display rows) and only calls the
 * server's read endpoints (Steps 1-3) plus the single `switch` mutation
 * (Step 4). Nothing is persisted between steps; closing the wizard discards the
 * in-flight choices.
 *
 * Rendering/gating (Wealthbox-linked household, `connections.slant`) is the
 * host placement's responsibility; this component assumes it is only mounted
 * when eligible. The Step-1 people come from AdviceCloud (the canonical store),
 * not a Wealthbox round-trip (APP-1329).
 */
export type WizardStep = 1 | 2 | 3 | 4;

const SwitchToSlantWidget = ({ householdId }: { householdId: number }) => {
  const { household } = useContext(HouseholdContext);
  const [step, setStep] = useState<WizardStep>(1);

  // Step 1 — the household's people the advisor confirms (read from AdviceCloud);
  // also the source side of Step 3's matching.
  const householdPeople = useHouseholdPeople(householdId);
  const people = useMemo(
    () => householdPeople.data?.data.people ?? [],
    [householdPeople.data],
  );

  // Wizard state accumulated across the steps, held in React only.
  const [slantHouseholdId, setSlantHouseholdId] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<SwitchDecision[]>([]);
  const [reviewRows, setReviewRows] = useState<ReviewRow[]>([]);
  const [slantPeople, setSlantPeople] = useState<SlantPerson[]>([]);

  // A sensible default for the Step 2 search box: the household name.
  const defaultQuery = household?.name ?? "";

  return (
    <Card>
      <Card.Header>
        <Card.Title className="mb-0">
          Switch to Slant — Step {step} of 4
        </Card.Title>
      </Card.Header>
      <Card.Body>
        {step === 1 && (
          <SwitchToSlantStep1
            people={people}
            isLoading={householdPeople.isPending}
            isError={householdPeople.isError}
            error={householdPeople.error}
            onConfirm={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <SwitchToSlantStep2
            householdId={householdId}
            defaultQuery={defaultQuery}
            onPicked={(pickedHouseholdId) => {
              setSlantHouseholdId(pickedHouseholdId);
              setStep(3);
            }}
          />
        )}

        {step === 3 && slantHouseholdId !== null && (
          <SwitchToSlantStep3
            householdId={householdId}
            slantHouseholdId={slantHouseholdId}
            people={people}
            onConfirmed={(confirmedDecisions, rows, people) => {
              setDecisions(confirmedDecisions);
              setReviewRows(rows);
              setSlantPeople(people);
              setStep(4);
            }}
          />
        )}

        {step === 4 && slantHouseholdId !== null && (
          <SwitchToSlantStep4
            householdId={householdId}
            slantHouseholdId={slantHouseholdId}
            rows={reviewRows}
            decisions={decisions}
            slantPeople={slantPeople}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default SwitchToSlantWidget;
